// stocksAdapters.ts — convert the Stocks DRF DTOs (snake_case, lowercase
// status enums, ISO dates) into the UI domain shapes. One small pure function
// per entity, same pattern as adapters.ts.

import { owingStageLabel, type PrescriptionDto, type PrescriptionLineDto } from './rxAdapters';
import type {
  Adjustment,
  Alert,
  AuditEntry,
  Backorder,
  BackorderStatus,
  DispenseLine,
  DispenseOrder,
  DispenseStage,
  DispenseStatus,
  InwardEntry,
  InwardLine,
  Movement,
  OtcSale,
  PartPack,
  RecallUi,
  StockAdjustment,
  StockAlert,
  StockReturnUi,
  StockTransfer,
  Tray,
  TrayStatus,
} from '@/domain/types';

/** ISO timestamp/date → en-GB display date (the prototype's format). */
export function ukDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
}

/** ISO timestamp → en-GB date + time. */
export function ukDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/** snake_case enum value → Title Case display label ("partially_fulfilled" → "Partially Fulfilled"). */
function titleCase(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

// --- MovementLedger → Movement ---

interface LedgerDto {
  id: string;
  store_id: string;
  sku: string;
  batch_id: string;
  batch_number?: string;
  qty_delta: number;
  source: 'inward' | 'outward' | 'adjustment' | 'transfer_out' | 'transfer_in';
  source_ref_id: string | null;
  notes: string;
  created_at: string;
  created_by: string | null;
}

const LEDGER_SOURCE_TO_TYPE: Record<LedgerDto['source'], Movement['type']> = {
  inward: 'Inward',
  outward: 'Outward',
  adjustment: 'Adjustment',
  transfer_out: 'Transfer',
  transfer_in: 'Transfer',
};

export function ledgerToMovement(d: unknown): Movement {
  const dto = d as LedgerDto;
  return {
    id: dto.id,
    type: LEDGER_SOURCE_TO_TYPE[dto.source] ?? 'Adjustment',
    product: dto.sku,
    batch: dto.batch_number || dto.batch_id,
    batchId: dto.batch_id,
    store: dto.store_id,
    qty: dto.qty_delta,
    ref: dto.source_ref_id ?? dto.notes ?? '—',
    by: dto.created_by ?? '—',
    date: ukDateTime(dto.created_at),
  };
}

// --- AuditLog → AuditEntry ---

interface AuditLogDto {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  created_at: string;
  created_by: string | null;
  actor_role?: string;
  device_info?: string;
}

const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Title-case a snake/dotted token: "stock_outward" → "Stock Outward". */
function humanizeToken(s: string): string {
  return s
    .replace(/[._]/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Internal-id / plumbing keys that are noise in a human audit line (they end in _id or are opaque).
const HIDDEN_AUDIT_KEYS = new Set([
  'placements',
  'idempotency_key',
  'source_ref_id',
  'snapshot_id',
]);

/** Humanise a single audit value for readable display. */
function shortAuditValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  if (_UUID_RE.test(s)) return s.slice(0, 8);
  // snake_case codes (e.g. "stocktake_variance") → "Stocktake Variance"; leave SKUs/refs as-is.
  if (/^[a-z]+(_[a-z]+)+$/.test(s)) return humanizeToken(s);
  return s;
}

// Preferred ordering for audit facts so the most useful keys read first; unlisted keys follow.
const FACT_ORDER = [
  'product',
  'sku',
  'batch_number',
  'bin_location',
  'qty_on_hand',
  'qty_delta',
  'new_on_hand',
  'units',
  'reason_code',
  'status',
  'moved_from_batch',
  'moved_from_bin',
  'note',
];
const factRank = (k: string): number => {
  const i = FACT_ORDER.indexOf(k);
  return i === -1 ? FACT_ORDER.length : i;
};

export function auditLogToEntry(d: unknown): AuditEntry {
  const dto = d as AuditLogDto;
  const after = dto.after_state ?? {};
  // Structured facts: humanise keys, humanise/shorten values, drop opaque internal ids (anything
  // ending in _id, plus known plumbing keys), and order by usefulness — for tidy chip display.
  const facts = Object.entries(after)
    .filter(([k]) => !HIDDEN_AUDIT_KEYS.has(k) && !k.endsWith('_id'))
    .sort(([a], [b]) => factRank(a) - factRank(b))
    .map(([k, v]) => ({ label: humanizeToken(k), value: shortAuditValue(v) }));
  const detail = facts.map((f) => `${f.label}: ${f.value}`).join(' · ');
  // Include full entity_id + any prescription_id/outward_id in after_state for entity matching
  const extraIds = [after.prescription_id, after.outward_id].filter(Boolean).join(' ');
  const ref = typeof after.reference === 'string' ? after.reference : '';
  return {
    id: dto.id,
    entity: `${dto.entity_type} ${dto.entity_id} ${extraIds}`.trim(),
    entityType: humanizeToken(dto.entity_type),
    entityId: dto.entity_id ? String(dto.entity_id).slice(0, 8) : '',
    ref: ref || undefined,
    action: dto.action,
    detail: detail || '—',
    facts,
    who: dto.created_by ?? '—',
    role: dto.actor_role || '—',
    device: dto.device_info || '',
    date: ukDateTime(dto.created_at),
    tone: 'info',
    evt: dto.action,
  };
}

// --- StockInward (+ lines) → InwardEntry ---

interface InwardDto {
  id: string;
  store_id: string;
  reference: string;
  supplier: string;
  status: string;
  posted_at: string | null;
  created_at: string;
}

interface InwardLineDto {
  id: string;
  inward: string;
  sku: string;
  batch_number: string;
  expiry_date: string;
  qty_received: number;
  scan_type: 'barcode' | 'manual' | 'ocr';
  verification_status: 'not_verified' | 'verified';
}

const SCAN_TYPE_LABEL: Record<InwardLineDto['scan_type'], InwardLine['scan']> = {
  barcode: 'Scan',
  manual: 'Manual',
  ocr: 'OCR',
};

function inwardLineToUi(dto: InwardLineDto): InwardLine {
  return {
    sku: dto.sku,
    product: dto.sku,
    batch: dto.batch_number,
    exp: ukDate(dto.expiry_date),
    qty: dto.qty_received,
    bin: '—',
    scan: SCAN_TYPE_LABEL[dto.scan_type] ?? 'Manual',
    status: dto.verification_status === 'verified' ? 'OK' : 'Expired – Approval Needed',
  };
}

// titleCase('ready_to_post') would yield 'Ready To Post' — map explicitly to
// the UI status union so gating like `status === 'Ready to Post'` holds.
const INWARD_STATUS_LABEL: Record<string, InwardEntry['status']> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  ready_to_post: 'Ready to Post',
  posted: 'Posted',
  voided: 'Voided',
};

export function inwardToEntry(d: unknown, allLines: unknown[]): InwardEntry {
  const dto = d as InwardDto;
  const lines = (allLines as InwardLineDto[]).filter((l) => l.inward === dto.id);
  return {
    id: dto.reference || dto.id.slice(0, 8),
    uuid: dto.id,
    date: ukDate(dto.created_at),
    supplier: dto.supplier || '—',
    po: dto.reference,
    store: dto.store_id,
    status: INWARD_STATUS_LABEL[dto.status] ?? (titleCase(dto.status) as InwardEntry['status']),
    by: '—',
    byRole: '—',
    received: ukDateTime(dto.posted_at ?? dto.created_at),
    lines: lines.map(inwardLineToUi),
    docs: [],
  };
}

// --- StockOutward → DispenseOrder / Tray ---

interface OutwardDto {
  id: string;
  store_id: string;
  reference: string;
  prescription_id: string | null;
  outward_type: string;
  status: string;
  tray_status: string;
  tray_number: string | null;
  tray_master: string | null;
  tray_last_station: string;
  tray_completed_at: string | null;
  tray_closed_at: string | null;
  tray_override_reason: string;
  assigned_pharmacist: string | null;
  assigned_pharmacist_name: string | null;
  assigned_sa: string | null;
  assigned_sa_name: string | null;
  ticket_number: string;
  ticket_generated_at: string | null;
  pack_source_overrides?: Record<string, string>;
  picking_completed_at: string | null;
  bench_location: string | null;
  bench_assigned_at: string | null;
  posted_at: string | null;
  collected_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Map a prescription line's backend status onto the dispense-line vocabulary. */
function rxLineToDispenseStatus(line: PrescriptionLineDto): DispenseLine['status'] {
  if (line.status === 'dispensed') {
    return line.qty_dispensed < line.qty_prescribed ? 'Dispensed (partial)' : 'Picked';
  }
  if (line.status === 'owing') {
    return line.qty_dispensed > 0 ? 'Owing' : 'Allocated';
  }
  if (line.status === 'incomplete') {
    // A short line still has its ALLOCATED portion to dispense now (partial supply) — treat it
    // as dispensable, not a blanket Owing. The true shortfall (prescribed − allocated) becomes
    // an owing backorder at post. Only a line with NOTHING reserved is un-dispensable.
    return line.qty_allocated > 0 ? 'Allocated' : 'Not Available';
  }
  if (line.status === 'product_not_available') return 'Not Available';
  if (line.scan_status === 'match') return 'Scanned';
  if (line.status === 'allocated') return 'Allocated';
  return 'Pending';
}

function rxLineToDispenseLine(line: PrescriptionLineDto): DispenseLine {
  return {
    lineId: line.id,
    sku: line.sku,
    product: line.drug_name || line.sku,
    fefoBatch: line.fefo_batch_number || '—',
    selectedBatch: line.allocated_batch_number || '—',
    exp: line.expiry_date ? ukDate(line.expiry_date) : '—',
    qty: line.qty_prescribed,
    status: rxLineToDispenseStatus(line),
    allocated: !!line.allocation_id,
    allocId: line.allocation_id,
    dispensedQty: line.qty_dispensed,
    pendingQty: Math.max(0, line.qty_prescribed - line.qty_dispensed),
    qtyAllocated: line.qty_allocated,
    allocations: (line.allocations ?? []).map((a) => ({
      allocationId: a.allocation_id,
      batchId: a.batch_id,
      batchNumber: a.batch_number,
      qty: a.qty,
      unitsPerPack: a.units_per_pack,
      expiryDate: a.expiry_date,
      scanStatus: a.scan_status,
      binLocation: a.bin_location,
    })),
  };
}

const RX_STATUS_STAGE: Record<string, { status: DispenseStatus; stage: DispenseStage }> = {
  awaiting_allocation: { status: 'Awaiting Allocation', stage: 'allocating' },
  incomplete: { status: 'Awaiting Allocation', stage: 'allocating' },
  allocated: { status: 'Ready to Pick', stage: 'ready' },
  picking: { status: 'Picking', stage: 'picking' },
  awaiting_accuracy_check: { status: 'Accuracy Check', stage: 'accuracy' },
  ready_for_dispense: { status: 'Accuracy Check', stage: 'accuracy' },
  ready_for_collection: { status: 'Waiting Collection', stage: 'bench' },
};

/** Map backend tray_status + outward status to the dispensing workflow stage.
 *  `allocated` = at least one line is reserved (so a draft with stock already on it
 *  — e.g. a backorder re-entry — reads as Ready to Pick, not Awaiting Allocation). */
function resolveStage(
  dto: OutwardDto,
  allocated: boolean,
  rxStatus?: string,
): { status: DispenseStatus; stage: DispenseStage } {
  // Collection + void are the FINAL states and must win over `posted_at` — a collected job has
  // BOTH posted_at and collected_at set, so checking posted_at first (as before) hid every
  // collected job under "Posted" and left the Collected count at zero. Collection is signalled by
  // `collected_at` (the authoritative field); tray_status='closed' is the older tray-side signal.
  if (dto.tray_status === 'cancelled') return { status: 'Voided', stage: 'done' };
  if (dto.collected_at || dto.tray_status === 'closed')
    return { status: 'Collected', stage: 'collected' };
  if (dto.posted_at) return { status: 'Posted', stage: 'done' };
  // IMP-N35: with the linked script known, its status decides (the same source as the board and
  // the script page). tray_status alone is station telemetry (D6) and drifts from the script.
  const byRx = rxStatus ? RX_STATUS_STAGE[rxStatus] : undefined;
  if (byRx) return byRx;
  if (dto.tray_status === 'complete') return { status: 'Waiting Collection', stage: 'bench' };
  if (dto.tray_status === 'verification') return { status: 'Accuracy Check', stage: 'accuracy' };
  if (dto.tray_status === 'assembling') return { status: 'Picking', stage: 'picking' };
  // Draft with no tray yet: Ready to Pick once stock is allocated, else awaiting allocation.
  if (dto.status === 'draft' && !dto.tray_number) {
    return allocated
      ? { status: 'Ready to Pick', stage: 'ready' }
      : { status: 'Awaiting Allocation', stage: 'allocating' };
  }
  // active or allocated — stock allocated but picking hasn't started
  return { status: 'Ready to Pick', stage: 'ready' };
}

/** `rxById` (prescription uuid → DTO) enriches the bare outward header with the
 *  patient, prescriber, CD flag and lines that live on the Prescriptions side. */
export function outwardToDispenseOrder(
  d: unknown,
  rxById?: Map<string, PrescriptionDto>,
): DispenseOrder {
  const dto = d as OutwardDto;
  const rx = dto.prescription_id ? rxById?.get(dto.prescription_id) : undefined;
  const lines = (rx?.lines ?? []).map(rxLineToDispenseLine);
  const allocated = lines.length > 0 && lines.some((l) => l.allocated);
  const { status, stage } = resolveStage(dto, allocated, rx?.status);
  return {
    id: dto.reference || dto.id.slice(0, 8),
    uuid: dto.id,
    patient:
      rx?.patient_name || (dto.prescription_id ? `Rx ${dto.prescription_id.slice(0, 8)}` : '—'),
    rxDate: ukDate(rx?.prescribed_date ?? dto.created_at),
    prescriber: rx?.prescriber || '—',
    cd: rx?.is_cd ?? false,
    tray: dto.tray_number || null,
    store: dto.store_id,
    status,
    stage,
    approvals: 0,
    lines,
    // "Allocated enough to proceed" = at least one dispensable line. A partial Rx
    // (some lines out of stock → owing) must still move to tray/pick for the
    // available items; requiring every line allocated would strand the owing flow.
    allocated,
    packOverrides: dto.pack_source_overrides ?? {},
    ticket: dto.ticket_number || undefined,
    fulfil:
      rx && ['owing', 'partially_dispensed', 'incomplete'].includes(rx.status) ? 'Owing' : null,
    owing: rx?.owing
      ? {
          status: rx.owing.status,
          cause: rx.owing.cause,
          qtyPending: rx.owing.qty_pending,
          stageLabel: owingStageLabel(rx.owing.status, rx.owing.sub_status, rx.owing.cause),
        }
      : null,
    rxUuid: rx?.id,
    rxRef: rx?.prescription_id,
    stageEnteredAt: dto.updated_at || dto.created_at,
    assignedPicker: dto.assigned_sa || undefined,
    assignedPickerName: dto.assigned_sa_name || undefined,
    assignedChecker: dto.assigned_pharmacist || undefined,
    assignedCheckerName: dto.assigned_pharmacist_name || undefined,
  };
}

const TRAY_STATUS_LABEL: Record<string, TrayStatus> = {
  active: 'Active',
  assembling: 'Assembling',
  verification: 'Verification',
  complete: 'Complete',
  closed: 'Complete',
  cancelled: 'Cancelled',
};

const TRAY_STATUS_STAGE: Record<string, Tray['stage']> = {
  active: null,
  assembling: 'Assembling',
  verification: 'Verification',
  complete: 'Collection',
  closed: 'Collection',
  cancelled: null,
};

export function outwardToTray(d: unknown, rxMap?: Map<string, PrescriptionDto>): Tray {
  const dto = d as OutwardDto;
  const rx = dto.prescription_id && rxMap ? rxMap.get(dto.prescription_id) : undefined;
  return {
    id: dto.tray_number || dto.reference || dto.id.slice(0, 8),
    uuid: dto.id,
    rx: dto.prescription_id ? dto.prescription_id.slice(0, 8) : '—',
    prescriptionId: dto.prescription_id || undefined,
    patient: rx?.patient_name || undefined,
    trayNumber: dto.tray_number || undefined,
    status: TRAY_STATUS_LABEL[dto.tray_status] ?? 'Active',
    stage: TRAY_STATUS_STAGE[dto.tray_status] ?? null,
    station: dto.tray_last_station || null,
    fulfil: null,
    lastScan: ukDateTime(dto.updated_at),
    user: dto.assigned_sa_name || dto.assigned_pharmacist_name || '—',
    movements: [],
    items: [],
  };
}

// --- Backorder ---

interface BackorderDto {
  id: string;
  store_id: string;
  type: 'inbound' | 'outbound';
  cause?: string;
  severity?: string;
  sku: string;
  product_name?: string;
  free_stock?: number | null;
  prescription_id: string | null;
  prescription_ref?: string | null;
  qty_pending: number;
  qty_fulfilled: number;
  sub_status: string;
  status: string;
  expected_delivery_date: string | null;
  created_at: string;
}

// Lifecycle statuses that carry their own meaning once the backorder moves past
// "open" — show these directly. While still open we show the sub_status instead
// (Awaiting Supplier / Awaiting Patient Collection), which is more informative.
const BACKORDER_LIFECYCLE = new Set([
  'ready_to_pick',
  'stock_received',
  'ready_for_collection',
  'partially_fulfilled',
  'fulfilled',
  'closed',
  'cancelled',
]);

export function backorderToUi(d: unknown): Backorder {
  const dto = d as BackorderDto;
  const status = (
    BACKORDER_LIFECYCLE.has(dto.status)
      ? titleCase(dto.status)
      : dto.sub_status
        ? titleCase(dto.sub_status)
        : titleCase(dto.status)
  ) as BackorderStatus;
  const total = dto.qty_pending + dto.qty_fulfilled;
  return {
    id: dto.id.slice(0, 8),
    uuid: dto.id,
    type: dto.type === 'inbound' ? 'Inbound' : 'Outbound',
    cause: dto.cause || undefined,
    severity: dto.severity || undefined,
    sku: dto.sku,
    product: dto.product_name || dto.sku,
    freeStock: dto.free_stock ?? undefined,
    store: dto.store_id,
    status,
    date: ukDate(dto.created_at),
    supplier: dto.type === 'inbound' ? '—' : undefined,
    ordered: dto.type === 'inbound' ? total : undefined,
    received: dto.type === 'inbound' ? dto.qty_fulfilled : undefined,
    // Prefer the human prescription reference (RX-M-…) so the register is searchable/deep-linkable
    // by it; fall back to the UUID stub only when the ref isn't available.
    rx: dto.prescription_ref || (dto.prescription_id ? dto.prescription_id.slice(0, 8) : undefined),
    prescriptionId: dto.prescription_id || undefined,
    prescribedQty: dto.type === 'outbound' ? total : undefined,
    dispensedQty: dto.type === 'outbound' ? dto.qty_fulfilled : undefined,
    pendingQty: dto.type === 'outbound' ? dto.qty_pending : undefined,
    expectedDate: ukDate(dto.expected_delivery_date),
  };
}

// --- PartPack ---

interface PartPackUsageDto {
  qty: number;
  reference_type: string;
  reference_id: string | null;
  created_at: string;
}

interface PartPackDto {
  id: string;
  sku: string;
  batch_id: string;
  batch_number: string;
  original_qty: number;
  remaining_qty: number;
  reserved_qty?: number;
  free_qty?: number;
  state: 'open' | 'used' | 'consumed';
  last_used_at: string | null;
  created_by: string | null;
  // enrichment joined from batch + product master
  expiry_date?: string | null;
  units_per_pack?: number;
  product_name?: string;
  uom?: string;
  usages: PartPackUsageDto[];
}

export function partPackToUi(d: unknown): PartPack {
  const dto = d as PartPackDto;
  const reserved = dto.reserved_qty ?? 0;
  return {
    id: dto.id.slice(0, 8),
    uuid: dto.id,
    sku: dto.sku,
    product: dto.product_name || dto.sku,
    batch: dto.batch_number || dto.batch_id.slice(0, 8),
    original: dto.original_qty,
    remaining: dto.remaining_qty,
    reserved,
    free: dto.free_qty ?? Math.max(0, dto.remaining_qty - reserved),
    packSize: dto.units_per_pack ?? dto.original_qty,
    uom: dto.uom || 'unit',
    expiry: dto.expiry_date ? ukDate(dto.expiry_date) : '—',
    lastUsed: ukDate(dto.last_used_at),
    openedBy: dto.created_by ?? '—',
    state: titleCase(dto.state) as PartPack['state'],
    ledger: (dto.usages ?? []).map((u) => ({
      qty: u.qty,
      time: ukDateTime(u.created_at),
      rx: u.reference_id ? u.reference_id.slice(0, 8) : '—',
    })),
  };
}

// --- Transfer → StockTransfer ---

interface TransferDto {
  id: string;
  store_id: string;
  source_store_id: string;
  dest_store_id: string;
  reference: string;
  sku: string;
  batch_number: string;
  requested_qty: number;
  status: string;
  created_at: string;
}

const TRANSFER_STATUS_LABEL: Record<string, StockTransfer['status']> = {
  draft: 'Draft',
  awaiting_approval: 'Awaiting Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  in_transit: 'In Transit',
  discrepancy: 'Discrepancy',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function transferToUi(d: unknown): StockTransfer {
  const dto = d as TransferDto;
  return {
    id: dto.reference || dto.id.slice(0, 8),
    uuid: dto.id,
    source: dto.source_store_id,
    dest: dto.dest_store_id,
    date: ukDate(dto.created_at),
    status: TRANSFER_STATUS_LABEL[dto.status] ?? 'Draft',
    by: '—',
    dir: 'out',
    items: [
      {
        sku: dto.sku,
        product: dto.sku,
        batch: dto.batch_number,
        qty: dto.requested_qty,
        bin: '—',
      },
    ],
  };
}

// --- Adjustment (apps.adjustments) → StockAdjustment / inventory Adjustment ---

interface AdjustmentDto {
  id: string;
  store_id: string;
  batch_stock_id: string;
  sku: string;
  product_name?: string;
  batch_number: string;
  qty_delta: number;
  reason_code: string;
  status: string;
  requested_by: string | null;
  approved_by: string | null;
  created_at: string;
}

export function adjustmentToUi(d: unknown): StockAdjustment {
  const dto = d as AdjustmentDto;
  return {
    id: dto.id.slice(0, 8),
    uuid: dto.id,
    sku: dto.sku || '—',
    product: dto.product_name || dto.sku || '',
    batch: dto.batch_number || dto.batch_stock_id.slice(0, 8),
    store: dto.store_id,
    delta: dto.qty_delta,
    reason: titleCase(dto.reason_code),
    comment: '',
    status:
      dto.status === 'applied' || dto.status === 'approved'
        ? 'Applied'
        : dto.status === 'rejected'
          ? 'Rejected'
          : 'Pending Approval',
    by: dto.requested_by ?? '—',
    byRole: '—',
    approver: dto.approved_by,
    date: ukDate(dto.created_at),
    cd: false,
  };
}

/** Same DTO into the Inventory module's Adjustment shape. */
export function adjustmentToInventoryUi(d: unknown): Adjustment {
  const stk = adjustmentToUi(d);
  return {
    id: stk.id,
    uuid: stk.uuid,
    // Show the resolved product name (falls back to the sku when unresolved).
    product: stk.product || stk.sku,
    batch: stk.batch,
    store: stk.store,
    delta: stk.delta,
    reason: stk.reason,
    comment: stk.comment,
    status: stk.status === 'Applied' ? 'Approved' : stk.status,
    by: stk.by,
    byRole: stk.byRole,
    approver: stk.approver,
    date: stk.date,
    cd: stk.cd,
  };
}

// --- Derived stock alert (/api/inventory/alerts/) → Alert / StockAlert ---

interface StockAlertDto {
  id: string;
  type: 'expired' | 'expiring' | 'blocked' | 'low_stock' | 'overstock';
  severity: 'error' | 'warning' | 'info';
  status?: 'open' | 'acknowledged';
  sku: string;
  product_name?: string;
  batch_id: string;
  batch_number: string;
  store_id: string;
  bin_location: string;
  qty_on_hand: number;
  days_to_expiry: number;
  message: string;
  expiry_is_default?: boolean;
}

// Human labels for the full StockAlertTypeEnum (audit #5: 'low_stock' leaked
// raw into alert headlines and type chips). Title-case to match the Alerts
// page's ALERT_TYPES filter vocabulary.
const ALERT_TYPE_LABEL: Record<StockAlertDto['type'], string> = {
  expired: 'Expired',
  expiring: 'Expiring',
  blocked: 'Blocked',
  low_stock: 'Low Stock',
  overstock: 'Overstock',
};

/** Label for an alert-type enum; unknown future values are humanised
 *  (snake_case → Title Case), never leaked raw. */
const alertTypeLabel = (t: string) => ALERT_TYPE_LABEL[t as StockAlertDto['type']] ?? titleCase(t);

/** Sentence-case variant used in alert headlines ('Low stock — …'), matching
 *  the mock-derived titles in computeAlerts. */
const sentenceCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

export function stockAlertToInventoryAlert(d: unknown): Alert {
  const dto = d as StockAlertDto;
  // Label reads as the product name; the raw sku is kept as a field but no longer the headline.
  const name = dto.product_name || dto.sku;
  const label = alertTypeLabel(dto.type);
  return {
    id: dto.id,
    type: label,
    tone: dto.severity,
    product: name,
    sku: dto.sku,
    batch: dto.batch_id,
    store: dto.store_id,
    t: `${sentenceCase(label)} — ${name}`,
    meta: `Batch ${dto.batch_number}${dto.bin_location ? ` · ${dto.bin_location}` : ''} · ${dto.message}`,
    status: dto.status ?? 'open',
    expiryIsDefault: dto.expiry_is_default ?? false,
  };
}

export function stockAlertToStockAlert(d: unknown): StockAlert {
  const dto = d as StockAlertDto;
  const name = dto.product_name || dto.sku;
  const label = alertTypeLabel(dto.type);
  return {
    id: dto.id,
    // Sentence case to match the STOCKS feed vocabulary (STK_ALERT_ICON keys).
    type: sentenceCase(label),
    tone: dto.severity,
    store: dto.store_id,
    sku: dto.sku,
    t: `${sentenceCase(label)} — ${name}`,
    meta: `Batch ${dto.batch_number}${dto.bin_location ? ` · ${dto.bin_location}` : ''} · ${dto.message}`,
    status: 'open',
  };
}

// --- StockReturn / Recall (returns & recalls register) ---

interface StockReturnDto {
  id: string;
  store_id: string;
  type: 'patient' | 'supplier';
  sku: string;
  batch_number: string;
  qty: number;
  reason: string;
  condition: string;
  reference: string;
  adjustment_id: string | null;
  status: 'quarantined' | 'restocked' | 'disposal' | 'rma_drafted' | 'disposed' | 'returned';
  created_at: string;
}

const RETURN_STATUS_LABEL: Record<StockReturnDto['status'], StockReturnUi['status']> = {
  quarantined: 'Quarantined',
  restocked: 'Restocked',
  disposal: 'Disposal',
  rma_drafted: 'RMA Drafted',
  disposed: 'Disposed',
  returned: 'Returned',
};

export function stockReturnToUi(d: unknown): StockReturnUi {
  const dto = d as StockReturnDto;
  return {
    id: dto.id.slice(0, 8),
    uuid: dto.id,
    type: dto.type === 'patient' ? 'Patient' : 'Supplier',
    sku: dto.sku,
    product: dto.sku,
    batch: dto.batch_number,
    qty: dto.qty,
    reason: dto.reason,
    condition: dto.condition || undefined,
    reference: dto.reference || undefined,
    adjustmentId: dto.adjustment_id ? dto.adjustment_id.slice(0, 8) : null,
    status: RETURN_STATUS_LABEL[dto.status] ?? 'Quarantined',
    store: dto.store_id,
    date: ukDate(dto.created_at),
  };
}

interface RecallDto {
  id: string;
  sku: string;
  batch_number: string;
  reason: string;
  status: 'active' | 'resolved';
  batches_locked: number;
  allocations_released: number;
  tasks_total?: number;
  tasks_done?: number;
  created_at: string;
  created_by: string | null;
}

export function recallToUi(d: unknown): RecallUi {
  const dto = d as RecallDto;
  return {
    id: dto.id.slice(0, 8),
    uuid: dto.id,
    sku: dto.sku,
    batch: dto.batch_number,
    reason: dto.reason,
    status: dto.status === 'resolved' ? 'Resolved' : 'Active',
    batchesLocked: dto.batches_locked,
    allocationsReleased: dto.allocations_released,
    tasksTotal: dto.tasks_total ?? 0,
    tasksDone: dto.tasks_done ?? 0,
    date: ukDate(dto.created_at),
    by: dto.created_by ?? '—',
  };
}

// --- StocktakeSession → ReconSession (UI) ---

interface StocktakeDto {
  id: string;
  store_id: string;
  reference: string;
  scope: string;
  session_type?: 'full_cycle' | 'partial';
  period_start?: string | null;
  period_end?: string | null;
  status: 'in_progress' | 'closed';
  closed_at: string | null;
  created_at: string;
  counts: { resolution: string }[];
}

export interface ReconSessionUi {
  id: string;
  uuid?: string;
  scope: string;
  sessionType: 'full_cycle' | 'partial';
  periodStart: string | null;
  periodEnd: string | null;
  store: string;
  snapCount: number;
  status: 'In Progress' | 'Closed';
  started: string;
  closed: string | null;
}

export function stocktakeToReconSession(d: unknown): ReconSessionUi {
  const dto = d as StocktakeDto;
  return {
    id: dto.reference || dto.id.slice(0, 8),
    uuid: dto.id,
    scope: dto.scope || 'Cycle count',
    sessionType: dto.session_type === 'full_cycle' ? 'full_cycle' : 'partial',
    periodStart: dto.period_start ?? null,
    periodEnd: dto.period_end ?? null,
    store: dto.store_id,
    snapCount: dto.counts?.length ?? 0,
    status: dto.status === 'closed' ? 'Closed' : 'In Progress',
    started: ukDateTime(dto.created_at),
    closed: dto.closed_at ? ukDateTime(dto.closed_at) : null,
  };
}

/** OTC sale row derived from an `otc`-type outward header (qty lives on the ledger). */
export function outwardToOtcSale(d: unknown): OtcSale {
  const dto = d as OutwardDto;
  return {
    id: dto.reference || dto.id.slice(0, 8),
    sku: '—',
    product: '',
    qty: 0,
    legal: '—',
    store: dto.store_id,
    date: ukDateTime(dto.posted_at ?? dto.created_at),
  };
}
